import { useRef } from "react";

import { Guid } from "@/Common/Models/Guid";
import { Patient } from "@/Common/Models/Patient/Patient";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { ApssFormStrategy, BaseFormsPopupStrategy, FormsPopupController, PdfFormPopupStrategy } from "@/Patient/Components/MedicalNote/Forms/FormsPopupController";
import { RadPdfFormsSelectionComponent } from "@/Patient/Components/MedicalNote/Forms/RadPdf/RadPdfFormsSelectionComponent";
import { WorkSafeBcInvalidFieldsPopup } from "@/Patient/Components/MedicalNote/WorkSafeBc/SubComponent/WorkSafeBcInvalidFieldsPopup";
import { WorkSafeBcController } from "@/Patient/Components/MedicalNote/WorkSafeBc/WorkSafeBcController";
import { FormType } from "@/Patient/Models/Forms/FormTypeEnum";
import { DisplayContextEnum } from "@/Patient/Models/MedicalNote/Enum/DisplayContextEnum";
import { SignedAuthorizationDocumentType } from "@/Patient/Models/MedicalNote/Enum/SignedAuthorizationDocumentType";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { ObstetricalFileController } from "../Obstetric/Form/File/ObstetricalFileController";
import { ApssFormSection } from "./Apss/ApssFormSection";
import { MedicalNoteFormsRegistryView } from "./Extensions/MedicalNoteFormsRegistry";
import { RadPdfFormSection } from "./RadPdf/RadPdfFormSection";
import { RadiologyFormsController } from "./RadiologyFormsController";

interface MedicalNoteFormProps {
    noteModel: IPatientMedicalNoteModel;
    medicalSummaryModel: IPatientMedicalSummaryModel;
    patientModel: Patient;
    immunizations: MedicalItemImmunization[];
    currentEncounter: Encounter;
    canEdit: boolean;
    isReadOnly: boolean;
    isEditMode: boolean;
    isShowWorkSafeBcInvalidFieldsPopup: boolean;
    patientId: Guid;
    crdsSelectedSupervisorContactId: string;
    formsPopupOpen: boolean;
    formsPopupType: FormType;
    workSafeBcController: React.RefObject<WorkSafeBcController>;
    isSectionOpen: boolean;
    radPdfFormSelectionPopupOpen?: boolean;
    displayContext: DisplayContextEnum;
    mutateChangeFormPropsState: (formsPopupOpen: boolean, formsPpopupType: FormType) => void;
    getRevokeSignaturePopupModel: (id: string, type: SignedAuthorizationDocumentType) => void;
    createNewWorkSafeBcFormClaim: () => void;
    setShowWorkSafeBcInvalidFieldsPopup: (isVisible: boolean) => void;
    onOpenAPSSForm: () => void;
    onAddFormClick: () => void;
    toggleSectionOpen: () => void;
    onContinueRadPdfFormSelectionPopup?: () => void;
    onDuplicateRadPdfFormSelectionPopup?: () => void;
    onCloseRadPdfFormSelectionPopup?: () => void;
}

function MedicalNoteForm(props: MedicalNoteFormProps): JSX.Element {
    const {
        radPdfFormSelectionPopupOpen,
        onContinueRadPdfFormSelectionPopup,
        onDuplicateRadPdfFormSelectionPopup,
        onCloseRadPdfFormSelectionPopup
    } = props;
    const callbackList = useRef([]);

    function runCallbackList(): void {
        callbackList.current.forEach(callback => callback());
        callbackList.current = [];
    }

    function onAPSSFormReload(): void {
        patientStore.ActionsMedicalNote.ReloadApssForms(props.currentEncounter.Id);
    }

    function closePdfFormsPopup(): void {
        props.mutateChangeFormPropsState(false, props.formsPopupType);
    }

    function emptySeparationRow(): JSX.Element {
        return <tr><td style={{ height: "10px" }} /></tr>;
    }

    function isWorkSafeBcFormEnabled(): boolean {
        const encounterSite = cacheStore.GetSiteById(props.currentEncounter.SiteId);

        return cacheStore.ClinicSettings.EnableWorkSafeBcFormFeature && encounterSite?.isSiteInProvince(CanadaState.BritishColumbia);
    }

    function getFormPopupStrategy(): BaseFormsPopupStrategy {
        let strategy: BaseFormsPopupStrategy = null;

        if (props.formsPopupType == FormType.PDF) {
            strategy = new PdfFormPopupStrategy(
                () => closePdfFormsPopup(),
                FormType.PDF
            );
        }
        else if (props.formsPopupType == FormType.APSS) {
            strategy = new ApssFormStrategy(
                () => closePdfFormsPopup(),
                FormType.APSS,
                props.patientId,
                props.crdsSelectedSupervisorContactId
            );
        }

        return strategy;
    }

    const isSignedOff = props.noteModel.get("Encounter").IsSignedOff;
    const hasApssConfig = !IsNullOrWhiteSpace(sessionStore.getCurrentEmployee().getActiveSite().Model.ClinicHistoricalIds);

    const patientForms = props.noteModel.get("PatientForms") || [].toImmutable();
    const patientApssForms = props.noteModel.get("PatientAPSSForms") || [].toImmutable();
    const isEmpty = patientForms.count() == 0
        && patientApssForms.count() == 0
        && !Object.values(props.noteModel.get("FormsCountByModule") ?? {}).some(count => count > 0)
        && !props.noteModel.get("ObstetricalFileModel")
        && (!isWorkSafeBcFormEnabled() || !props.noteModel.get("WorkSafeBcForms")?.any());

    // Only render sub section if the note is not signed off or if the subsection has some content
    const shouldRenderApssForms = patientApssForms.count() > 0 || (!isSignedOff && hasApssConfig);

    const isUserGdePilot = cacheStore.ClinicSettings.GdeNoteFormPilotUsers?.split(",").includes(cacheStore.GetCurrentEmployee().ContactFacet_id.toUpperCase());
    const shouldRenderRadiologyForms = !isSignedOff && (cacheStore.ClinicSettings.EnableGdeNoteForm || isUserGdePilot);

    // dont render forms section if note is signed off and it has no forms.
    return (isSignedOff && isEmpty) ? null : (
        <>
            <div className="note-block medical-note-block">
                {props.formsPopupOpen && (
                    <FormsPopupController
                        onCloseCallback={() => closePdfFormsPopup()}
                        formType={props.formsPopupType}
                        formPopupStrategy={getFormPopupStrategy()}
                    />
                )}

                {radPdfFormSelectionPopupOpen && (
                    <RadPdfFormsSelectionComponent
                        encounter={props.currentEncounter}
                        onContinue={(form, isFormAddedToEncounter) => {
                            callbackList.current.push(() => sessionStore.dispatch(LocalPatientOperations.MedicalNote.ContinueRadPdfForm, { form, isFormAddedToEncounter }));
                            onContinueRadPdfFormSelectionPopup?.();
                            runCallbackList();
                        }}
                        onDuplicate={form => {
                            callbackList.current.push(() => sessionStore.dispatch(LocalPatientOperations.MedicalNote.DuplicateRadPdfForm, { form }));
                            onDuplicateRadPdfFormSelectionPopup?.();
                            runCallbackList();
                        }}
                        onClose={() => onCloseRadPdfFormSelectionPopup?.()}
                    />
                )}

                <div className="note-block-header" onClick={() => props.toggleSectionOpen()}>
                    <div className="pull-right">
                        <span className={"glyphicon " + (props.isSectionOpen ? "glyphicon-chevron-up" : "glyphicon-chevron-down")} />
                    </div>
                    <h3>{cacheStore.GetString("FormsLabel").toUpperCase()}</h3>
                </div>

                {props.isSectionOpen && (
                    <div className="patient-note">
                        <table className="width-100">
                            <tbody>
                                <tr>
                                    <td className="c1">
                                        <strong>{cacheStore.GetString("FormsLabel")}</strong>
                                    </td>
                                    <td className="c2">
                                        <div className="tb-row tb">
                                            <div className="add-btn-column">
                                                {props.canEdit &&
                                                    <a onClick={() => props.onAddFormClick()}>[+]</a>
                                                }
                                            </div>
                                            {patientForms.count() > 0 && (
                                                <RadPdfFormSection
                                                    noteModel={props.noteModel}
                                                    prescriptions={props.medicalSummaryModel.get("Prescriptions")}
                                                    isReadOnly={props.isReadOnly}
                                                    isEditMode={props.isEditMode}
                                                    patientModel={props.patientModel}
                                                    patientId={props.patientId}
                                                    currentEncounter={props.currentEncounter}
                                                    getRevokeSignaturePopupModel={(id: string, type: SignedAuthorizationDocumentType) =>
                                                        props.getRevokeSignaturePopupModel(id, type)
                                                    }
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                {emptySeparationRow()}
                                {shouldRenderApssForms && (
                                    <>
                                        <tr>
                                            <td className="c1">
                                                <strong>{cacheStore.GetString("APSSLABEL")}</strong>
                                            </td>
                                            <td className="c2">
                                                <div className="tb-row tb">
                                                    <div className="add-btn-column">
                                                        {props.canEdit && hasApssConfig ?
                                                            <a onClick={() => props.onOpenAPSSForm()}> [+]</a>
                                                            : ""
                                                        }
                                                    </div>
                                                    {!props.noteModel.get("IsCreatingAPSSForm") &&
                                                        patientApssForms.count() > 0 &&
                                                        (
                                                            <ApssFormSection
                                                                patientAPSSForms={props.noteModel.get("PatientAPSSForms").toArray()}
                                                                patientModel={props.patientModel}
                                                                isReadOnly={props.isReadOnly}
                                                            />
                                                        )
                                                    }
                                                    {props.noteModel.get("IsCreatingAPSSForm") && (
                                                        <div className="bold underline margin-top-5px">
                                                            <a
                                                                style={{ color: "orangered" }}
                                                                onClick={() => onAPSSFormReload()}
                                                            >
                                                                {cacheStore.GetString("CRDSReload")}
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {emptySeparationRow()}
                                    </>
                                )}
                                <MedicalNoteFormsRegistryView
                                    isEditMode={props.isEditMode}
                                    isReadOnly={props.isReadOnly}
                                    encounter={props.currentEncounter}
                                    patientModel={props.patientModel}
                                    patientContactId={props.patientId}
                                    displayContext={props.displayContext}
                                />
                                {isWorkSafeBcFormEnabled() && props.isShowWorkSafeBcInvalidFieldsPopup && (
                                    <WorkSafeBcInvalidFieldsPopup
                                        onCreateClaim={() => props.createNewWorkSafeBcFormClaim()}
                                        onClose={() => props.setShowWorkSafeBcInvalidFieldsPopup(false)}
                                    />
                                )}
                                {isWorkSafeBcFormEnabled() && (
                                    <WorkSafeBcController
                                        ref={props.workSafeBcController}
                                        patient={props.patientModel}
                                        encounter={props.noteModel.get("Encounter")}
                                        isEditMode={props.isEditMode}
                                        isReadOnly={props.isReadOnly}
                                        model={props.noteModel.get("WorkSafeBcForms")}
                                    />
                                )}
                                {shouldRenderRadiologyForms && (
                                    <RadiologyFormsController
                                        patient={props.patientModel}
                                        siteId={props.currentEncounter.SiteId}
                                    />
                                )}
                                <ObstetricalFileController
                                    noteModel={props.noteModel}
                                    medicalSummaryModel={props.medicalSummaryModel}
                                    patientModel={props.patientModel}
                                    isReadOnly={props.isReadOnly}
                                    isEditMode={props.isEditMode}
                                    isNotInteractive={props.isReadOnly}
                                    siteId={props.currentEncounter.SiteId}
                                    immunizations={props.immunizations}
                                />
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <div className="padding-sm" />
        </>
    );
}

export { MedicalNoteForm };
